// The page's game state on the client core: the login flow, the world session and
// the E key, and the client-side rules (attack cooldowns and costs, quest markers, prompts), and the arrows and
// fireballs in flight. The page calls frame() once per animation frame and draws what it returns.
import { DayNight } from '../../../shared/src/dayNight';
import { BuffKind, buffDefinition, LifeState } from '../../../shared/src/rules/buffs';
import {
  CombatEventType, EntityKind, Facing, GameConstants, HarvestPhase, NoticeKind, OffhandKind, QuestStatus, TileEntityType, ToolKind,
  WeaponClass, ZoneFlags, enumName,
} from '../../../shared/src/constants';
import { GameContent, content, useContent, type ItemDefinition, type ProjectileStats } from '../core/content';
import { cachedContent } from '../core/contentBundle';
import { InteractionController, type OpenRequest } from '../core/interaction';
import { log } from '../core/log';
import { chunkSize, tilesPerChunk } from '../core/map';
import { facingFor, feetTile, solidAt } from '../../../shared/src/motor';
import { ClientSession, SessionState } from '../core/session';
import { StdbWorldLink } from '../core/stdb/worldLink';
import { accountName, keepGuestToken, type SpacetimeOptions } from './stdbLogin';
import { BrowserStorage } from '../core/storage';
import { Money } from '../core/rules/economy';
import { Equipment, ToolBelt, attackCostProblem, attackStatsOf, equippedAttack, missileAngles, type AttackStats } from '../core/rules/items';
import { TalentAttacks, attackKindOf, describeEffect } from '../core/rules/talents';
import { Leveling, questsGivenBy } from '../core/rules/progress';
import { MobRules, TileEntities, tileEntityDef, type TileEntity } from '../../../shared/src/rules/world';
import { UiColors } from '../core/rules/tooltip';
import type { EntityGather } from '../core/content';

export const storage = new BrowserStorage();

/** How long a swing is shown (the page's Effects SWING): the character faces the swing for as long. */
const swingDuration = 0.28;
/** Where swings start, relative to the feet (the chest). */
const swingOriginY = -6;
/** How far an arrow or fireball's flight is checked in one go (pixels). */
const flightStep = 2;

type Event = Record<string, unknown>;

/**
 * An arrow or fireball in flight, as this page shows it. Flown here, straight from the shooter, stopping at a solid tile,
 * the first mob it touches as drawn, or the weapon's reach; the server flies its own and decides what's hit.
 */
interface Flight {
  x: number; y: number; dx: number; dy: number; angle: number; left: number; age: number; shot: ProjectileStats;
  /** Whether it has left its starting point (where it starts doesn't count as a wall, as on the server). */
  moved: boolean;
}

class Game {
  session: ClientSession | null = null;

  readonly interaction = new InteractionController();
  private events: Event[] = [];
  private readonly dirtyChunks = new Set<string>();
  private zoneSerial = 0;
  private message: string | null = null;
  private messageIsError = false;
  private worldName = '';
  private harvestRule: Pick<EntityGather, "tool"> | undefined;
  private nextAttackTime = 0;
  private lastWeaponHint = -10;
  private hurtUntil = 0;
  private localSwingUntil = 0;
  private localSwingFacing = Facing.Down;
  /** Whether the server was last told we're blocking (null: tell it again, as after a zone change). */
  private blockSent: boolean | null = null;
  private blockWanted = false;
  private flights: Flight[] = [];
  private lastNoticeTime = -1;
  private readonly backdrop = new Map<string, Uint16Array>();

  /** Why the player is back at the login screen, shown there once. */
  takeMessage(): { text: string | null; error: boolean } {
    const m = { text: this.message, error: this.messageIsError };
    this.message = null;
    return m;
  }

  // ---- content ---------------------------------------------------------------------------------------------------

  /**
   * The page's files (the content it shipped with): they go in effect, then the content a world last sent, if the
   * browser kept one (worlds send theirs when it differs).
   */
  async init(names: string[], texts: string[]): Promise<{ fingerprint: number; problems: string[] }> {
    const shipped = new Map<string, string>();
    names.forEach((n, i) => { if (n.endsWith('.json')) shipped.set(n.slice(n.lastIndexOf('/') + 1), texts[i]); });
    let c = new GameContent(shipped);
    const kept = await cachedContent(storage);
    if (kept) c = kept;
    useContent(c);
    log.info(`Content loaded: ${c.items.size} items, ${c.recipes.size} recipes, ${c.tiles.tiles.length} tiles (fingerprint ${c.fingerprint.toString(16).padStart(8, '0')})`);
    return { fingerprint: c.fingerprint, problems: [...c.problems] };
  }

  // ---- joining ---------------------------------------------------------------------------------------------------

  /** Leaves the world and forgets the sign-in's session; with a message, the login screen shows it. */
  logout(message: string | null): void {
    this.leaveWorld(null, false);
    if (message !== null) { this.message = message; this.messageIsError = false; }
  }

  /**
   * Joins the world (SpacetimeDB: its identity is the account; there's no world list). With no
   * token, as a guest: SpacetimeDB issues one, which is kept for next time.
   */
  startSpacetime(o: SpacetimeOptions, token: string | undefined, guest: boolean): void {
    this.leaveWorld(null, false);
    this.worldName = 'Ashenheart';
    const opened = StdbWorldLink.connect({ uri: o.uri, database: o.database }, token, t => { if (guest) keepGuestToken(o, t); });
    this.attach(ClientSession.over(opened, `${o.uri}/${o.database}`, guest ? 'guest' : accountName(token), storage));
  }

  // ---- the world session -----------------------------------------------------------------------------------------

  private attach(s: ClientSession): void {
    this.session = s;
    s.on('chunkChanged', (cx, cy) => this.dirtyChunks.add(`${cx},${cy}`));
    this.blockSent = null;
    s.on('zoneEntered', name => {
      this.zoneSerial++;
      this.flights = [];
      this.dirtyChunks.clear();
      this.interaction.reset();
      this.blockSent = null;
      this.events.push({ type: 'zone', name, dark: s.world.isDark });
    });
    s.on('harvestState', (phase, x, y, ticks) => this.onHarvestState(phase, x, y, ticks));
    s.on('combatEvent', (type, entity, other, value) => this.onCombatEvent(type, entity, other, value));
    s.on('questsChanged', (accepted, completed) => this.events.push({ type: 'quests', accepted, completed }));
    s.on('expGained', amount => this.events.push({ type: 'exp', amount }));
    s.on('moneyGained', copper => this.events.push({ type: 'money', text: Money.format(copper), color: UiColors.coin(Money.coins(copper)[0].coin) }));
    this.lastNoticeTime = -1;
  }

  /** Leaves the world (or stops joining one). With a message, the login screen shows it. */
  leaveWorld(message: string | null, isError: boolean): void {
    if (this.session) {
      this.session.disconnect();
      this.session = null;
    }
    this.events = [];
    this.flights = [];
    this.dirtyChunks.clear();
    this.interaction.reset();
    if (message !== null) { this.message = message; this.messageIsError = isError; }
  }

  sessionState(): object {
    const s = this.session;
    if (!s) return { state: 'none' };
    const state = s.state === SessionState.Connecting || s.state === SessionState.LoggingIn ? 'connecting'
      : s.state === SessionState.CharacterSelect ? 'characters' : s.state === SessionState.InGame ? 'game' : 'disconnected';
    return {
      state, world: this.worldName, server: s.serverName, maxCharacters: s.maxCharacters, error: s.characterError,
      disconnect: s.disconnectMessage, characters: s.characters.map(c => ({ name: c.name, appearance: c.appearance, lastPlayed: c.lastPlayed })),
    };
  }

  /** Pumps the session outside the game (joining, character select). */
  pump(dt: number): void { this.session?.update(dt, 0); }

  takeDirtyChunks(): number[] {
    const list: number[] = [];
    for (const k of this.dirtyChunks) list.push(...k.split(',').map(Number));
    this.dirtyChunks.clear();
    return list;
  }

  /** A loaded chunk's tiles: 1024 unsigned 16-bit IDs for each ground layer 1-3 then the objects, or empty if it isn't loaded. */
  chunk(cx: number, cy: number): Uint16Array {
    const c = this.session?.world.map.get(cx, cy);
    if (!c) return new Uint16Array(0);
    const bytes = new Uint16Array(tilesPerChunk * 4);
    c.layers.forEach((layer, i) => bytes.set(layer, i * tilesPerChunk));
    return bytes;
  }

  worldObjects(): object {
    const w = this.session?.world;
    return {
      tileEntities: w ? [...w.tileEntities.values()].map(e => ({
        id: e.id, type: e.type, x: e.x, y: e.y, data: e.data, label: e.label,
        hidden: e.type === TileEntityType.Portal && (e.data & TileEntities.portalHidden) !== 0,
      })) : [],
      bags: w ? [...w.bags.values()].map(b => ({ id: b.id, x: b.x, y: b.y, count: b.contents.length })) : [],
    };
  }

  // ---- the login backdrop (Content/login.json: the editor's login map and how the menus show it) -------------------

  /**
   * Reads the login map: its three ground layers and object grid as renderer chunks, its entities, where the camera centres (its Spawn) and the
   * panning settings. A broken or missing file gives { error } and the menus go without.
   */
  loadLoginMap(text: string): object {
    this.backdrop.clear();
    try {
      const doc = JSON.parse(text) as Record<string, unknown>, map = (doc.map ?? {}) as Record<string, unknown>;
      const w = Number(map.width), h = Number(map.height);
      if (!Number.isInteger(w) || !Number.isInteger(h) || w < 3 || h < 3 || w > 4096 || h > 4096) throw new Error('bad login map size');
      const layers = ['ground', 'ground2', 'ground3', 'objects'].map(name => {
        const rows = Array.isArray(map[name]) ? map[name] as unknown[][] : [];
        return (x: number, y: number) => { const v = Number(rows[y]?.[x] ?? 0); return Number.isInteger(v) && v >= 0 && v <= 65535 ? v : 0; };
      });
      const chunks: [number, number][] = [];
      for (let cy = 0; cy * chunkSize < h; cy++)
        for (let cx = 0; cx * chunkSize < w; cx++) {
          const tiles = new Uint16Array(tilesPerChunk * 4);
          for (let ty = 0; ty < chunkSize; ty++)
            for (let tx = 0; tx < chunkSize; tx++) {
              const x = cx * chunkSize + tx, y = cy * chunkSize + ty;
              if (x >= w || y >= h) continue;
              layers.forEach((at, layer) => { tiles[layer * tilesPerChunk + ty * chunkSize + tx] = at(x, y); });
            }
          this.backdrop.set(`${cx},${cy}`, tiles);
          chunks.push([cx, cy]);
        }
      const spawn = (Array.isArray(map.triggers) ? map.triggers as Record<string, unknown>[] : []).find(t => t.type === 'Spawn');
      const entities = (Array.isArray(map.entities) ? map.entities as Record<string, unknown>[] : []).flatMap(e => {
        const def = content().entityByKey(String(e.definition ?? ''));
        return def ? [{ id: String(e.id), definition: def.id, x: Number(e.x) || 0, y: Number(e.y) || 0, offsetX: Number(e.offsetX) || 0, offsetY: Number(e.offsetY) || 0, depleted: false }] : [];
      });
      const lighting = String(map.lighting ?? (map.dark ? 'Dark' : 'Outdoors'));
      // Point lights placed on the map (Light triggers), as the world sends a zone's.
      const lights = (Array.isArray(map.triggers) ? map.triggers as Record<string, unknown>[] : []).filter(t => t.type === 'Light').map(t => ({
        x: (Number(t.x) || 0) * GameConstants.tileSize + (Number(t.offsetX ?? 8) || 0), y: (Number(t.y) || 0) * GameConstants.tileSize + (Number(t.offsetY ?? 8) || 0),
        radius: Math.max(0, Math.min(255, Number(t.radius) || 0)), color: /^#[0-9a-f]{6}$/i.test(String(t.color)) ? parseInt(String(t.color).slice(1), 16) : 0xffffff, flicker: t.flicker === true }));
      const num = (v: unknown, d: number, lo: number, hi: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.max(lo, Math.min(hi, v)) : d);
      return {
        w, h, chunks, entities, lights, dark: lighting === 'Dark', indoors: lighting === 'Indoors',
        x: (Number(spawn?.x ?? w / 2) + 0.5) * GameConstants.tileSize, y: (Number(spawn?.y ?? h / 2) + 0.5) * GameConstants.tileSize,
        autoPan: doc.autoPan !== false, panSpeed: num(doc.panSpeed, 1, 0, 10), panRadius: num(doc.panRadius, 38, 0, 512), zoom: num(doc.zoom, 0, 0, 8),
      };
    } catch (e) {
      log.warn(`No login backdrop: ${(e as Error).message}`);
      return { error: (e as Error).message };
    }
  }

  backdropChunk(cx: number, cy: number): Uint16Array { return this.backdrop.get(`${cx},${cy}`) ?? new Uint16Array(0); }

  // ---- one frame of play -----------------------------------------------------------------------------------------

  /**
   * Advances the session (network, prediction, interpolation) and E-key targeting, then describes the frame: where
   * everyone is, what E would do, the HUD numbers and what happened (events).
   */
  frame(dt: number, buttons: number, eHeld: boolean, ePressed: boolean, blocked: boolean): object {
    const s = this.session;
    if (!s) return { state: 'none' };
    s.update(dt, s.life.state === LifeState.Dead ? 0 : buttons & 0x1f); // the dead lie still until they release
    if (s.state === SessionState.Disconnected) return { state: 'disconnected', message: s.disconnectMessage };
    if (s.state !== SessionState.InGame) return { state: 'joining' };

    if (s.world.hasZone) {
      const open = this.interaction.update(s, eHeld, ePressed, blocked);
      if (open) this.events.push(this.open(s, open));
    }
    this.collectNotices(s);
    this.fly(s, dt);

    const now = s.time, w = s.world, local = s.local, st = local.state;
    const [tx, ty] = feetTile(local.x, local.y);
    const inv = s.inventory;
    const npcs: object[] = [];
    for (const e of w.tileEntities.values()) {
      const npc = e.type === TileEntityType.Npc ? content().npc(e.data) : undefined;
      if (!npc) continue;
      // NPCs are tile entities drawn as characters: they face a player who comes close.
      const [cx] = TileEntities.center(e);
      const fx = cx, fy = (e.y + 1) * GameConstants.tileSize - 2;
      const dx = local.renderX - fx, dy = local.renderY - fy;
      const [marker, markerColor] = this.npcMarker(s, npc.id);
      npcs.push({
        id: e.id, x: fx, y: fy, name: npc.name, appearance: npc.appearance,
        f: dx * dx + dy * dy < 48 * 48 ? facingFor(dx, dy) : Facing.Down, marker, markerColor, armor: outfitSlots(npc.outfit),
      });
    }
    // Buffs now (by entity), and where we stand through death.
    const tickNow = s.clock.estimatedTick, active = new Map<number, { kind: number; startTick: number; endTick: number }[]>();
    for (const b of s.buffs.values()) if (b.endTick > tickNow) {
      const list = active.get(b.entityId) ?? []; list.push(b); active.set(b.entityId, list);
    }
    const has = (id: number, kind: BuffKind) => (active.get(id) ?? []).some(b => b.kind === kind);
    const life = s.life, ghost = life.state === LifeState.Ghost, dead = life.state === LifeState.Dead;
    const frame = {
      state: 'game', t: now, rev: s.revision, zoneSerial: this.zoneSerial,
      zone: { id: w.zoneId, name: w.zoneName, dark: w.isDark, indoors: w.isIndoors, instanced: (w.flags & ZoneFlags.Instanced) !== 0, instance: w.instanceId, w: w.map.widthTiles, h: w.map.heightTiles, time: s.timeOfDay, clock: DayNight.format(s.timeOfDay), lights: w.lights },
      me: {
        id: local.entityId, name: local.name, level: s.level, x: local.renderX, y: local.renderY, tx, ty,
        f: now < this.localSwingUntil ? this.localSwingFacing : st.facing, moving: st.moving, running: st.running, anim: local.animTime,
        appearance: local.appearance, flash: now < this.hurtUntil - 0.2, armor: inv.armor.map(a => a.item),
        stunned: st.stunTicks > 0, blocking: !!this.blockSent && st.stunTicks === 0, shield: content().item(Equipment.shield(inv))?.icon ?? '',
        ghost, burning: has(local.entityId, BuffKind.Burning), shielded: has(local.entityId, BuffKind.Rekindled),
      },
      ents: [...w.entities.values()].map(e => e.kind === EntityKind.Mob
        ? { id: e.id, mob: true, x: e.x, y: e.y, moving: e.moving, hp: e.healthPct, level: e.level, flash: now < e.hitFlashUntil, type: e.mobType, f: e.facing, levelColor: this.mobLevelColor(s, e.level) }
        : {
          id: e.id, mob: false, x: e.x, y: e.y, moving: e.moving, hp: e.healthPct, level: e.level, flash: now < e.hitFlashUntil,
          name: e.name, appearance: e.appearance, f: now < e.swingUntil ? e.swingFacing : e.facing, running: e.running, anim: e.animTime,
          party: s.isInMyParty(e.name), armor: e.armor, stunned: e.stunned, blocking: e.blocking,
          shield: e.blocking ? this.anyShieldIcon() : '',
          ghost: e.ghost, burning: has(e.id, BuffKind.Burning), shielded: has(e.id, BuffKind.Rekindled),
        }),
      npcs,
      resources: [...w.map.placedEntities.values()],
      shots: this.flights.map(p => ({
        x: p.x, y: p.y, angle: p.angle, sprite: p.shot.sprite, age: p.age, lightRadius: p.shot.lightRadius, lightColor: p.shot.lightColor,
      })),
      zones: [...s.healZones.values()].map(z => {
        const zone = content().item(z.item)!.spell!.zone!, span = Math.max(1, (z.endTick - z.startTick) >>> 0);
        return { x: z.x, y: z.y, radius: zone.radius, color: zone.color,
          life: Math.max(0, Math.min(1, (s.clock.estimatedTick - z.startTick) / span)) };
      }),
      corpses: [...s.corpses].map(([id, c]) => ({ id, x: c.x, y: c.y, appearance: c.appearance, mine: id === local.entityId })),
      life: { state: ghost ? 'ghost' as const : dead ? 'dead' as const : 'alive' as const, corpseMap: life.corpseMap,
        corpseX: life.corpseX, corpseY: life.corpseY, corpseHere: s.corpses.has(local.entityId) },
      hover: this.hover(s),
      hud: {
        hp: s.health, maxHp: s.maxHealth, mana: s.mana, maxMana: s.maxMana, stamina: st.staminaUnits / 30, maxStamina: 100, exhausted: st.exhausted, level: s.level, exp: s.exp,
        next: Leveling.expToNext(s.level), maxLevel: s.level >= Leveling.maxLevel, money: s.money, ping: s.ping,
        online: Math.max(1, s.players.length), maxPlayers: s.maxPlayers, server: s.serverName, admin: s.isAdmin, god: s.godMode,
        hurt: Math.max(0, this.hurtUntil - now), unspent: s.unspentTalentPoints,
        auras: s.auras.map(id => content().talent(id)).filter(n => !!n).map(n => ({ name: n!.name, text: n!.effects.map(describeEffect).join(', ') })),
        buffs: (active.get(local.entityId) ?? []).map(b => {
          const def = buffDefinition(b.kind)!, span = Math.max(1, (b.endTick - b.startTick) >>> 0);
          return { kind: b.kind, name: def.name, description: def.description, debuff: def.debuff,
            seconds: Math.max(0, Math.min(def.durationTicks, b.endTick - tickNow) / GameConstants.tickRate), fraction: Math.max(0, Math.min(1, (b.endTick - tickNow) / span)) };
        }).sort((a, b) => Number(a.debuff) - Number(b.debuff) || a.kind - b.kind),
        bytesIn: s.statistics.bytesReceived,
        bytesOut: s.statistics.bytesSent, pending: local.pendingInputs.length, corrections: local.corrections,
        tick: s.clock.estimatedTick, chunks: w.map.count, epoch: w.epoch,
      },
      events: this.blockedHits(this.events),
    };
    this.events = [];
    return frame;
  }

  private open(s: ClientSession, o: OpenRequest): Event {
    if (o.bag) return { type: 'open', what: 'bag', id: o.bag.id };
    const e = o.station!;
    switch (e.type) {
      case TileEntityType.Workbench:
      case TileEntityType.Furnace: return { type: 'open', id: e.id, what: 'crafting' };
      case TileEntityType.Portal:
        s.sendUsePortal(e.id); // the zone change follows
        return { type: 'open', id: e.id, what: 'portal' };
      case TileEntityType.Npc: {
        const npc = content().npc(e.data);
        s.sendTalkNpc(e.id); // a shop visit still counts as talking to them (quests)
        if (npc && npc.stock.length > 0) return { type: 'open', id: e.id, what: 'vendor', npc: npc.id };
        s.dialogue = null; // shown once the server answers
        return { type: 'open', id: e.id, what: 'npc', npc: e.data };
      }
      default: return { type: 'open', id: e.id, what: enumName(TileEntityType, e.type).toLowerCase() };
    }
  }

  /** What E would do (a label above the target, in world pixels), or the harvest progress bar. */
  private hover(s: ClientSession): object | undefined {
    const ts = GameConstants.tileSize, i = this.interaction;
    const active = i.activeTile, progress = i.progress(s.time);
    if (active && progress !== null)
      return { kind: 'progress', x: active[0] * ts + 1, y: active[1] * ts - 5, progress, tx: active[0], ty: active[1] };
    if (i.hoverBag) {
      const n = i.hoverBag.contents.length;
      return { kind: 'bag', id: i.hoverBag.id, label: `[E] Open bag (${n} ${n === 1 ? 'item' : 'items'})`, x: i.hoverBag.x, y: i.hoverBag.y - 16 };
    }
    if (i.hoverEntity) {
      const he: TileEntity = i.hoverEntity, verb = tileEntityDef(he.type)?.verb ?? '';
      const npc = he.type === TileEntityType.Npc ? content().npc(he.data) : undefined;
      const label = npc && npc.stock.length > 0 ? `[E] Trade with ${npc.name}` : npc ? `[E] ${verb} to ${npc.name}`
        : he.type === TileEntityType.Portal ? `[E] ${verb} to ${he.label}` : `[E] ${verb}`;
      // NPCs carry a name tag overhead, so their prompt goes below their feet; or below the player's, when the player
      // stands below them (it would cover the player).
      const under = npc && s.local.renderY > (he.y + 1) * ts;
      return {
        kind: 'entity', id: he.id, entityType: enumName(TileEntityType, he.type).toLowerCase(), label,
        x: under ? s.local.renderX : TileEntities.center(he)[0], y: under ? s.local.renderY + 2 : npc ? (he.y + 1) * ts + 2 : he.y * ts - 3, below: !!npc,
      };
    }
    if (i.hoverCorpse && !i.isBusy) {
      const corpse = s.corpses.get(s.local.entityId);
      if (corpse) return { kind: 'tile', label: '[E] Rekindle', x: corpse.x, y: corpse.y - 14 };
    }
    const hv = i.hover;
    const rule = i.hoverResource ? content().entity(i.hoverResource.definition)?.gather : undefined;
    if (hv && rule && !i.isBusy && s.time > this.nextAttackTime + 1.0) {
      const hasTool = rule.tool === ToolKind.None || s.inventory.hasToolEquipped(rule.tool);
      const label = hasTool ? `[E] ${rule.verb}` : `Needs ${ToolBelt.slotName(ToolBelt.slotFor(rule.tool))}`;
      return { kind: 'tile', label, error: !hasTool, x: hv[0] * ts + ts / 2, y: hv[1] * ts - 3 };
    }
    return undefined;
  }

  private collectNotices(s: ClientSession): void {
    for (const n of s.notices) {
      if (n.time <= this.lastNoticeTime) continue;
      this.lastNoticeTime = n.time;
      this.events.push({ type: 'notice', kind: enumName(NoticeKind, n.kind).toLowerCase(), text: n.text });
    }
  }

  private onHarvestState(phase: HarvestPhase, x: number, y: number, ticks: number): void {
    const s = this.session!;
    // Remember the gathered entity tool: by the time Completed arrives the resource may already be depleted.
    if (phase === HarvestPhase.Started) {
      const resource = s.world.map.placedEntities.get(this.interaction.activeResourceId);
      this.harvestRule = resource ? content().entity(resource.definition)?.gather ?? undefined : undefined;
    }
    const tool = this.harvestRule?.tool ?? ToolKind.None;
    this.events.push({ type: 'harvest', phase: enumName(HarvestPhase, phase).toLowerCase(), x, y, tool: enumName(ToolKind, tool).toLowerCase() });
    this.interaction.onServerState(phase, x, y, ticks, s.time);
  }

  private onCombatEvent(type: CombatEventType, entity: number, other: number, value: number): void {
    const s = this.session!;
    const now = s.time;
    const isLocal = entity === s.local.entityId;
    const ent = s.world.entities.get(entity);
    const pos = isLocal ? { x: s.local.renderX, y: s.local.renderY } : ent ? { x: ent.x, y: ent.y } : null;
    switch (type) {
      case CombatEventType.Swing: {
        const def = content().item(other), stats = attackStatsOf(def);
        if (!ent || !def?.weapon) break;
        ent.swingUntil = now + swingDuration + 0.05;
        ent.swingFacing = facingFor(Math.cos(value), Math.sin(value));
        this.events.push(swingEvent(entity, def, value));
        if (stats?.projectile) this.launch(ent.x, ent.y + swingOriginY, value, stats);
        break;
      }
      case CombatEventType.Cast: {
        const stats = attackStatsOf(content().item(other));
        if (!ent || (!stats?.projectile && !stats?.zone)) break;
        this.events.push(castEvent(entity, stats));
        if (stats.projectile) this.launch(ent.x, ent.y + swingOriginY, value, stats);
        break;
      }
      case CombatEventType.Heal:
        if (pos && value > 0) this.events.push({ type: 'heal', entity, local: isLocal, amount: Math.max(1, Math.round(value)), x: pos.x, y: pos.y });
        break;
      case CombatEventType.Hit:
        if (!pos) break;
        if (isLocal) this.hurtUntil = now + 0.35;
        else if (ent) ent.hitFlashUntil = now + 0.15;
        // Armour leaves fractions: show whole points, and at least 1 for any damage.
        this.events.push({ type: 'hit', entity, local: isLocal, mine: other === s.local.entityId, damage: value > 0 ? Math.max(1, Math.round(value)) : 0, x: pos.x, y: pos.y });
        break;
      case CombatEventType.Block:
      case CombatEventType.Stun:
        if (pos) this.events.push({ type: type === CombatEventType.Block ? 'block' : 'stun', entity, local: isLocal, raw: value, x: pos.x, y: pos.y });
        break;
      case CombatEventType.LevelUp:
        if (!pos) break;
        if (ent) ent.level = Math.trunc(value);
        this.events.push({ type: 'levelup', entity, local: isLocal, level: Math.trunc(value), x: pos.x, y: pos.y });
        break;
      case CombatEventType.Died:
        if (!pos) break;
        this.events.push({ type: 'died', entity, local: isLocal, mob: ent?.kind === EntityKind.Mob, x: pos.x, y: pos.y });
        break;
    }
  }

  /**
   * A swing (or shot) toward a world point (left click / Space). Checks there's a weapon on the belt, the cooldown (the
   * server's whole ticks, or swings drift early and get refused) and what it costs (mana, an arrow), shows it at once
   * and tells the server.
   */
  /** Dead where you fell: release the corpse and rise as a ghost at the nearest graveyard. */
  releaseCorpse(): void { if (this.session?.life.state === LifeState.Dead) this.session.sendReleaseCorpse(); }

  attack(worldX: number, worldY: number): void {
    const s = this.session;
    if (!s || s.state !== SessionState.InGame || this.blockSent || s.local.state.stunTicks > 0 || s.life.state !== LifeState.Alive) return;
    const now = s.time;
    const weapon = equippedAttack(s.inventory);
    if (typeof weapon === 'string') {
      if (now - this.lastWeaponHint > 2) {
        this.lastWeaponHint = now;
        s.notices.push({ kind: NoticeKind.Error, text: weapon, time: now });
      }
      return;
    }
    if (now < this.nextAttackTime) return;
    const bonuses = s.talentBonuses, kind = attackKindOf(content().item(weapon.source));
    const price = TalentAttacks.cost(weapon.stats.manaCost, kind, bonuses), cost = attackCostProblem(weapon.stats, s.inventory, s.mana, price, s.health);
    if (cost) {
      if (now - this.lastWeaponHint > 1) {
        this.lastWeaponHint = now;
        s.notices.push({ kind: NoticeKind.Error, text: cost, time: now });
      }
      return;
    }
    this.nextAttackTime = now + TalentAttacks.cooldownTicks(weapon.stats.cooldownSeconds, kind, bonuses) * GameConstants.tickDelta;
    s.mana = Math.max(0, s.mana - price.mana); // until the server's stats say otherwise
    let dx = worldX - s.local.renderX, dy = worldY - (s.local.renderY + swingOriginY);
    if (dx * dx + dy * dy < 0.01) { dx = 0; dy = 1; }
    const angle = Math.atan2(dy, dx);
    this.localSwingUntil = now + swingDuration + 0.05;
    this.localSwingFacing = facingFor(dx, dy);
    s.local.face(this.localSwingFacing); // and keep facing that way afterwards (the server turns too, on that input)
    s.sendAttack(angle, Math.hypot(worldX - s.local.renderX, worldY - s.local.renderY));
    this.events.push(swingEvent(s.local.entityId, content().item(weapon.weapon)!, angle));
    if (weapon.source !== weapon.weapon) this.events.push(castEvent(s.local.entityId, weapon.stats));
    if (weapon.stats.projectile) this.launch(s.local.renderX, s.local.renderY + swingOriginY, angle, weapon.stats);
  }

  /**
   * Holding a block (right mouse, called every frame): tells the server when it starts and stops, and turns toward the
   * mouse (a world point) while standing. Without a shield it says so once per press. Blocking stops attacks; a stun
   * lowers the shield until it wears off (the server's rule too).
   */
  block(want: boolean, worldX: number, worldY: number): void {
    const s = this.session;
    if (!s || s.state !== SessionState.InGame) return;
    if (s.life.state !== LifeState.Alive) want = false;
    const hasShield = Equipment.shield(s.inventory) !== 0;
    if (want && !this.blockWanted && !hasShield && s.time - this.lastWeaponHint > 1) {
      this.lastWeaponHint = s.time;
      s.notices.push({ kind: NoticeKind.Error, text: 'Equip a shield in your offhand slot (beside a one-handed weapon) to block.', time: s.time });
    }
    this.blockWanted = want;
    const on = want && hasShield;
    if (on !== this.blockSent) { this.blockSent = on; s.sendBlock(on); }
    const st = s.local.state;
    if (on && !st.moving && st.stunTicks === 0) {
      const facing = facingFor(worldX - s.local.renderX, worldY - (s.local.renderY + swingOriginY));
      if (facing !== st.facing) s.local.face(facing);
    }
  }

  /**
   * A blocked hit arrives as a Hit (damage taken) and a Block or Stun (damage before armour) in the same update, in no
   * set order: marks the hit blocked, with how much the armour took off, both in whole points.
   */
  private blockedHits(events: Event[]): Event[] {
    for (const b of events) {
      if (b.type !== 'block' && b.type !== 'stun') continue;
      const hit = events.find(e => e.type === 'hit' && e.entity === b.entity && !e.blocked);
      if (hit) { hit.blocked = true; hit.reduced = Math.max(0, Math.round(b.raw as number) - (hit.damage as number)); }
    }
    return events;
  }

  /** What another player's raised shield looks like (only worn armour is replicated, so: the first shield there is). */
  private anyShieldIcon(): string {
    for (const d of content().items.values()) if (d.offhand === OffhandKind.Shield) return d.icon;
    return '';
  }

  /** An attack's arrow, fireball, or a spell's fan of missiles. */
  private launch(x: number, y: number, angle: number, stats: AttackStats): void {
    for (const a of missileAngles(stats, angle))
      this.flights.push({ x, y, dx: Math.cos(a), dy: Math.sin(a), angle: a, left: stats.reach, age: 0, shot: stats.projectile!, moved: false });
  }

  /** Moves the arrows and fireballs on; one that hits something ends with an 'impact' event where it stopped. */
  private fly(s: ClientSession, dt: number): void {
    if (this.flights.length === 0) return;
    const ts = GameConstants.tileSize, map = s.world.map;
    const mobs = [...s.world.entities.values()].filter(e => e.kind === EntityKind.Mob);
    this.flights = this.flights.filter(p => {
      p.age += dt;
      const travel = Math.min(p.shot.speed * dt, p.left);
      for (let d = 0; ; d = Math.min(d + flightStep, travel)) {
        const x = p.x + p.dx * d, y = p.y + p.dy * d;
        const wall = (d > 0 || p.moved) && solidAt(map, x, y);
        const mob = wall ? undefined : mobs.find(m => {
          const r = (content().mob(m.mobType)?.hitRadius ?? 6) + p.shot.radius, ox = x - m.x, oy = y - (m.y + MobRules.hitCenterOffsetY);
          return ox * ox + oy * oy <= r * r;
        });
        if (wall || mob) {
          this.events.push({ type: 'impact', x, y, mob: !!mob, glow: p.shot.lightRadius > 0, color: p.shot.lightColor, sprite: p.shot.sprite });
          return false;
        }
        if (d >= travel) break;
      }
      p.x += p.dx * travel;
      p.y += p.dy * travel;
      p.left -= travel;
      p.moved = true;
      return p.left > 0;
    });
  }

  /** Mob level tag colour: grey when it gives you no exp, warmer the further above you it is. */
  private mobLevelColor(s: ClientSession, mobLevel: number): string {
    const diff = mobLevel - s.level;
    if (!Leveling.earnsExp(s.level, mobLevel)) return '#8c8480';
    return diff >= 3 ? '#f05a50' : diff >= 1 ? '#fab450' : diff <= -3 ? '#8cc878' : '#ffffff';
  }

  /** Quest marker over an NPC: "!" has a quest to offer, "?" is tracking one (gold when it can be handed in). */
  private npcMarker(s: ClientSession, npc: number): [string | null, string | null] {
    let offer = false, active = false;
    for (const q of questsGivenBy(npc)) {
      const status = s.questStatus(q);
      if (status === QuestStatus.Completable) return ['?', '#ffd246'];
      if (status === QuestStatus.Offerable) offer = true;
      else if (status === QuestStatus.Active) active = true;
    }
    return offer ? ['!', '#ffd246'] : active ? ['?', '#aaa096'] : [null, null];
  }
}

/** A swing, or for a ranged weapon a shot (a staff's is silent: the spell's cast event sounds). */
function swingEvent(entity: number, def: ItemDefinition, angle: number): Event {
  const weapon = def.weapon!;
  if (weapon.weaponClass === WeaponClass.Staff) return { type: 'shot', entity, icon: def.icon, angle, sound: '' };
  return weapon.projectile ? { type: 'shot', entity, icon: def.icon, angle, sound: shotSound(weapon.projectile) }
    : { type: 'swing', entity, icon: def.icon, angle, reach: weapon.reach, arc: weapon.arcDegrees };
}
/** A spell leaving a staff (its sound; the staff's shot event shows it held out): a healing circle chimes. */
const castEvent = (entity: number, stats: AttackStats): Event => ({ type: 'cast', entity, sound: stats.zone ? 'sfx_heal' : shotSound(stats.projectile!) });
/** Something that glows whooshes like a fireball; anything else twangs off a bowstring. */
const shotSound = (shot: ProjectileStats) => shot.lightRadius > 0 ? 'sfx_fireball' : 'sfx_bow';

/** The worn armour list the renderer takes (one entry per armour slot) from an NPC's outfit. */
export function outfitSlots(outfit: number[]): number[] {
  const slots = Equipment.slots.map(() => 0);
  for (const item of outfit) {
    const i = Equipment.slotFor(content().item(item)?.armor ?? 0);
    if (i >= 0) slots[i] = item;
  }
  return slots;
}

export const game = new Game();
